import type { InventoryVehicle } from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, { createContext, useMemo, useState, useCallback } from 'react';
import type { RecentVehicle } from '~/contracts/home';

type RecentVehiclesContextType = {
  fetchRecentVehiclesTrigger: number;
  recentVehiclesFromRcb: LoadableState<RecentVehicle[]>;
  recentVehiclesFromSearch: LoadableState<InventoryVehicle[]>;
  triggerFetchRecentVehicles: () => void;
};

export const defaultContext: RecentVehiclesContextType = {
  fetchRecentVehiclesTrigger: 0,
  recentVehiclesFromRcb: defaultLoadableState<RecentVehicle[]>([]),
  recentVehiclesFromSearch: defaultLoadableState<InventoryVehicle[]>([]),
  triggerFetchRecentVehicles() {}
};

const RecentVehiclesContext =
  createContext<RecentVehiclesContextType>(defaultContext);

export function RecentVehiclesContextProvider({
  children
}: React.PropsWithChildren) {
  const recentVehiclesFromRcb = useLoadableState<RecentVehicle[]>([]);

  const recentVehiclesFromSearch = useLoadableState<InventoryVehicle[]>([]);

  const [fetchRecentVehiclesTrigger, setTrigger] = useState(
    defaultContext.fetchRecentVehiclesTrigger
  );

  const triggerFetchRecentVehicles = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<RecentVehiclesContextType>(
    () => ({
      fetchRecentVehiclesTrigger,
      recentVehiclesFromRcb,
      recentVehiclesFromSearch,
      triggerFetchRecentVehicles
    }),
    [
      fetchRecentVehiclesTrigger,
      recentVehiclesFromRcb,
      recentVehiclesFromSearch,
      triggerFetchRecentVehicles
    ]
  );

  return (
    <RecentVehiclesContext.Provider value={value}>
      {children}
    </RecentVehiclesContext.Provider>
  );
}

RecentVehiclesContextProvider.displayName = 'RecentVehiclesContextProvider';

export default RecentVehiclesContext;
